import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAdapter, useRecent, useSnapshot, useTopology } from "@/store/site";
import { PageShell } from "./PageShell";
import { Card } from "@/components/ui/Card";
import { KpiStrip } from "@/components/ui/KpiStrip";
import { HoldToConfirm } from "@/components/ui/HoldToConfirm";
import { MODE_CATALOG } from "@/data/modeCatalog";
import { ModeReasoningCard } from "@/components/ModeReasoningCard";
import { SetpointTracker, deviationSummary } from "@/components/charts/SetpointTracker";
import { pushToast } from "@/hooks/useToasts";
import { carryTo } from "@/lib/carryParams";
import { signedKW, isIdlePower, formatDateTime } from "@/lib/format";
import { cn } from "@/lib/cn";
import { useGlossary } from "@/lib/glossary";
import { tr, useT } from "@/lib/i18n";
import { HelpHint } from "@/components/ui/HelpHint";
import { ArrowRight, Cpu, Crosshair, Gauge, Zap } from "lucide-react";

// Config enums → operator sentences (CTL-05/CLAR-07), as DICT KEYS —
// translated at render time, never at module init. Unknown values fall
// through raw; the raw token always rides along in the row's title attr so
// support can still match it against controllerConfig.json.
const WATCHDOG_SENTENCE_KEYS: Record<string, string> = {
  zero_then_off: "manage.controller.watchdog.zeroThenOff",
};

const AUTO_BESS_ON_HINT_KEY = "manage.controller.autoBessOnHint";

// Static map — Tailwind can't see runtime-composed class names.
const HEALTH_TONE: Record<string, string> = {
  ok: "text-ok",
  warn: "text-warn",
  critical: "text-critical",
};

export default function ControllerPage() {
  const t = useT();
  const glossary = useGlossary();
  const snap = useSnapshot();
  const topology = useTopology();
  const adapter = useAdapter();
  const recent = useRecent();
  const overrideActive = snap.controller.schedule_source === "manual_override";
  const dev = deviationSummary(recent);
  const health = snap.controller.health;
  const fmtBool = (b: boolean) => t(b ? "manage.yes" : "manage.no");
  return (
    <PageShell title={t("nav.controller")} caption={t("manage.controller.caption")}>
      <ModeReasoningCard />

      <KpiStrip>
        <Tile icon={Crosshair} label={t("manage.controller.pidTarget")} value={`${signedKW(snap.controller.pid_setpoint_W)} kW`} title={t("manage.controller.pidTargetTitle")} />
        <Tile icon={Zap} label={t("manage.controller.gridNow")} value={`${signedKW(snap.grid.active_W)} kW`} title={t("manage.controller.gridNowTitle")} />
        <Tile
          icon={Cpu}
          label={t("manage.controller.autoBessOn")}
          value={fmtBool(snap.controller.auto_bess_on)}
          hint={<HelpHint title={t("manage.controller.autoBessOn")}>{t(AUTO_BESS_ON_HINT_KEY)}</HelpHint>}
        />
      </KpiStrip>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className={cn("p-5", overrideActive && "border-warn/50 ring-1 ring-warn/30")}>
          <div className="text-[11px] uppercase tracking-wider text-text-faint font-semibold">{t("manage.controller.status")}</div>
          <p className="mt-1 text-xs text-text-muted">{t("manage.controller.statusHint")}</p>
          <dl className="mt-4 grid grid-cols-2 gap-3">
            <Row term={t("manage.controller.mode")} def={MODE_CATALOG[snap.controller.active_mode]?.label ?? snap.controller.active_mode} title={MODE_CATALOG[snap.controller.active_mode]?.summary} />
            <Row term={t("manage.controller.autoBessOnRow")} def={fmtBool(snap.controller.auto_bess_on)} title={t(AUTO_BESS_ON_HINT_KEY)} />
            <Row term={t("manage.controller.activelyControlling")} def={fmtBool(snap.controller.pid_actively_controlling)} />
            <Row
              term={t("manage.controller.health")}
              def={
                health === "ok" ? (
                  <span className={HEALTH_TONE.ok}>{t("manage.controller.health.ok")}</span>
                ) : (
                  <Link
                    to={carryTo("/alarms")}
                    className={cn(
                      HEALTH_TONE[health] ?? "text-warn",
                      "underline decoration-dotted underline-offset-2 hover:opacity-80",
                    )}
                    title={t("manage.controller.healthTitle")}
                  >
                    {health === "warn" || health === "critical" ? t(`manage.controller.health.${health}`) : health}
                  </Link>
                )
              }
            />
            {/* Locale-stable via format.ts (DSGN-11) — raw toLocaleString()
                drifted from every other timestamp in the app. */}
            <Row term={t("manage.controller.overrideExpires")} def={snap.controller.override_expires_at ? formatDateTime(snap.controller.override_expires_at) : "—"} />
          </dl>
          {overrideActive && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Link
                to={carryTo("/schedule", { override: "1" })}
                className="flex h-10 items-center gap-1 rounded-md border border-edge bg-surface px-3 text-xs hover:border-accent/40"
              >
                {t("manage.controller.manageOverride")} <ArrowRight size={12} />
              </Link>
              <HoldToConfirm
                label={t("manage.controller.clearOverride")}
                intent="warn"
                onConfirm={async () => {
                  try {
                    await adapter.setOverride(null);
                    pushToast({ level: "ok", title: tr("manage.schedule.overrideCleared"), detail: tr("manage.controller.overrideClearedDetail") });
                  } catch (err) {
                    pushToast({ level: "critical", title: tr("manage.controller.overrideClearFailed"), detail: err instanceof Error ? err.message : String(err) });
                  }
                }}
              />
            </div>
          )}
        </Card>

        <Card className="p-5">
          <div className="text-[11px] uppercase tracking-wider text-text-faint font-semibold">{t("manage.controller.perBess")}</div>
          <p className="mt-1 text-xs text-text-muted">{t("manage.controller.perBessHint")}</p>
          <PerBessDistribution units={snap.bess_per_unit} />
        </Card>
      </div>

      <Card className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-text-faint font-semibold">{t("manage.controller.setpointTracking")}</div>
            <p className="mt-1 text-xs text-text-muted">{t("manage.controller.setpointHint")}</p>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-text-faint font-semibold">{t("manage.controller.cmdVsMeas")}</div>
            <div className="mt-0.5 text-[11px] text-text-muted num">
              {t("manage.controller.meanMax", { mean: dev.mean_kW.toFixed(1), max: dev.max_kW.toFixed(1) })}
            </div>
          </div>
        </div>
        <div className="mt-3">
          <SetpointTracker snapshots={recent} />
        </div>
      </Card>

      <Card className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-text-faint font-semibold">{t("manage.controller.guardrails")}</div>
            <p className="mt-1 text-xs text-text-muted">{t("manage.controller.guardrailsHint")}</p>
          </div>
          <Gauge size={20} className="text-accent" />
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-3">
          {topology.bess.map((b) => {
            const watchdogRaw = b.operating_profile?.watchdog_action ?? "zero_then_off";
            return (
              <Link key={b.id} to={carryTo(`/bess/${b.id}`)} className="card-hover block rounded-lg border border-edge bg-surface-2 p-3">
                <div className="text-xs font-semibold">{b.name}</div>
                <div className="mt-1 text-[11px] text-text-muted">
                  <div className="flex justify-between gap-3" title={t("manage.controller.rawConfig", { value: watchdogRaw })}>
                    <span className="shrink-0">{t("manage.controller.watchdog")}</span>
                    <strong className="text-right text-text">{WATCHDOG_SENTENCE_KEYS[watchdogRaw] ? t(WATCHDOG_SENTENCE_KEYS[watchdogRaw]) : watchdogRaw}</strong>
                  </div>
                  <div className="flex justify-between gap-3" title={t("manage.controller.cmdTimeoutTitle")}>
                    <span>{t("manage.controller.cmdTimeout")}</span>
                    <strong className="text-text">{b.operating_profile?.setpoint_timeout_s ?? 20} s</strong>
                  </div>
                  <div className="flex justify-between gap-3">
                    {/* The deployed battery service applies this limit in kW/s —
                        the old "pu/s" label was factually wrong (CTL-05); only
                        the on-wire field name still says pu_s. */}
                    <span className="flex items-center gap-1">
                      {t("manage.controller.rampLimit")}
                      <span onClick={(e) => e.stopPropagation()}>
                        <HelpHint title={glossary.rampLimit.term}>{glossary.rampLimit.def}</HelpHint>
                      </span>
                    </span>
                    <strong className="text-text">{b.operating_profile?.slew_fast_pu_s ?? 10} / {b.operating_profile?.slew_slow_pu_s ?? 2} kW/s</strong>
                  </div>
                  <div className="flex justify-between gap-3" title={t("manage.controller.rampThresholdTitle")}>
                    <span>{t("manage.controller.rampThreshold")}</span>
                    <strong className="text-text">{b.operating_profile?.slew_threshold_kW ?? 10} kW</strong>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="flex items-center gap-1">
                      {t("manage.controller.fullRefresh")}
                      <span onClick={(e) => e.stopPropagation()}>
                        <HelpHint title={glossary.interrogation.term}>{glossary.interrogation.def}</HelpHint>
                      </span>
                    </span>
                    <strong className="text-text">{b.operating_profile?.interrogation_period_s ?? 60} s</strong>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </Card>
    </PageShell>
  );
}

/**
 * Per-BESS dispatch bars.
 *
 * Two correctness traps the naive version fell into and this one avoids:
 *
 *  1. **Relative-to-fleet-max with no noise floor.** The old bar width was
 *     `|active_W| / max(|active_W| of every unit)`. When the whole fleet
 *     is idle, that max is itself sensor noise (~0.1 kW) so EVERY bar
 *     rendered at ~100 % — pure noise read as full dispatch. We now scale
 *     against the max of the *active* units only, and when no unit is
 *     above the idle deadband every bar is empty.
 *
 *  2. **Sign colour at the noise floor.** `active_W >= 0 ? charge : discharge`
 *     painted a unit sitting at +1 W charge-blue. Idle units now get a
 *     neutral bar + "idle" label; the charge/discharge colour only
 *     applies to units genuinely above the deadband.
 *
 * The card claims "SOC-weighted dispatch" — so we also surface each
 * unit's SOC inline, letting the operator actually verify the weighting
 * the copy describes instead of taking it on faith.
 *
 * Each row links to the unit's detail page (CTL-07) carrying the current
 * time window.
 */
function PerBessDistribution({
  units,
}: {
  units: { id: string; name: string; active_W: number; soc_pct: number }[];
}) {
  const t = useT();
  // Scale denominator: the largest magnitude among units that are
  // genuinely dispatching. Falls back so the division is always safe;
  // when every unit is idle this stays at the deadband and every bar
  // computes to a sub-1 % (effectively empty) width.
  const activeMax = Math.max(
    ...units.filter((u) => !isIdlePower(u.active_W)).map((u) => Math.abs(u.active_W)),
    0,
  );
  const fleetIdle = activeMax === 0;

  return (
    <div className="mt-4 space-y-2">
      {fleetIdle && (
        <div className="rounded-md border border-edge bg-surface-2/60 px-3 py-2 text-[11px] text-text-muted">
          {t("manage.controller.fleetIdle", { watts: 500 })}
        </div>
      )}
      {units.map((u) => {
        const idle = isIdlePower(u.active_W);
        const charging = !idle && u.active_W > 0;
        const widthPct = idle || activeMax === 0
          ? 0
          : Math.min(100, (Math.abs(u.active_W) / activeMax) * 100);
        return (
          <Link
            key={u.id}
            to={carryTo(`/bess/${u.id}`)}
            title={t("manage.controller.openDetail", { name: u.name })}
            className="-mx-2 grid grid-cols-[120px_1fr_92px] items-center gap-3 rounded-md px-2 py-1 transition-colors hover:bg-surface-2"
          >
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold truncate">{u.name}</span>
              <span
                className="text-[10px] num text-text-faint shrink-0"
                title={t("manage.controller.socTitle")}
              >
                {Math.round(u.soc_pct)}%
              </span>
            </div>
            <div className="h-2 rounded-full bg-surface-2">
              {!idle && (
                <div
                  className={cn(
                    "h-full rounded-full transition-[width] duration-500",
                    charging ? "bg-charge" : "bg-discharge",
                  )}
                  style={{ width: `${widthPct}%` }}
                />
              )}
            </div>
            <div
              className={cn(
                "text-right text-[11px] num",
                idle && "text-text-faint",
              )}
            >
              {idle ? t("manage.controller.idle") : `${signedKW(u.active_W, 1)} kW`}
            </div>
          </Link>
        );
      })}
    </div>
  );
}

function Tile({ icon: Icon, label, value, title, hint }: { icon: any; label: string; value: string; title?: string; hint?: ReactNode }) {
  return (
    <div className="card p-3" title={title}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-text-faint font-semibold">
        <Icon size={12} /> {label}
        {hint}
      </div>
      <div className="mt-1 text-base font-semibold num">{value}</div>
    </div>
  );
}

function Row({ term, def, title }: { term: string; def: ReactNode; title?: string }) {
  return (
    <div className="rounded-md border border-edge bg-surface-2 p-3" title={title}>
      <dt className="text-[10px] uppercase tracking-wider text-text-faint font-semibold">{term}</dt>
      <dd className="mt-1 text-sm font-semibold text-text num">{def}</dd>
    </div>
  );
}
